import { useEffect, useState, type FormEvent } from "react"
import { UserPlus01 } from "@untitledui/icons"
import type { TeamMemberSummary, TeamType } from "@/lib/api/types"
import { ROLES_BY_TEAM_TYPE } from "@/lib/team/roles"
import { DesignDialog } from "@/components/ui/design-dialog"
import { DesignSelect } from "@/components/ui/design-select"
import {
  Field,
  onboardingInputClass,
} from "@/components/onboarding/onboarding-shell"
import { cx } from "@/lib/utils/cx"

function memberDisplayName(m: TeamMemberSummary) {
  const name = [m.firstName, m.lastName].filter(Boolean).join(" ").trim()
  return name || m.email
}

interface ChangeRoleDialogProps {
  member: TeamMemberSummary | null
  teamName: string
  teamType: TeamType
  loading: boolean
  onClose: () => void
  onConfirm: (newRole: string) => void
}

// Role picker for PATCH /v1/teams/:teamId/members/:userId.
// Backend: shipped (serverapi tracker #49). Allowlist comes from
// roles.ts → ROLES_BY_TEAM_TYPE which mirrors the BE's validTeamRoles.
export function ChangeRoleDialog({
  member,
  teamName,
  teamType,
  loading,
  onClose,
  onConfirm,
}: ChangeRoleDialogProps) {
  const open = !!member
  const name = member ? memberDisplayName(member) : ""
  const roleOptions = ROLES_BY_TEAM_TYPE[teamType]
  const [role, setRole] = useState<string>(member?.role ?? roleOptions[0])

  useEffect(() => {
    if (open && member) setRole(member.role)
  }, [open, member])

  const unchanged = role === member?.role
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (unchanged) return
    onConfirm(role)
  }

  return (
    <DesignDialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <DesignDialog.Content width="md">
        <DesignDialog.Header>
          <DesignDialog.Title>Change role</DesignDialog.Title>
          <DesignDialog.Description>
            Updating the role for{" "}
            <span className="font-semibold text-zinc-700">{name}</span> on{" "}
            <span className="font-semibold text-zinc-700">{teamName}</span>.
          </DesignDialog.Description>
        </DesignDialog.Header>
        <form onSubmit={handleSubmit}>
          <DesignDialog.Body>
            <Field label="Role" required>
              <DesignSelect.Root value={role} onValueChange={setRole}>
                <DesignSelect.Trigger
                  className={cx(
                    onboardingInputClass,
                    "text-left flex items-center justify-between",
                    "data-[state=open]:border-emerald-500 data-[state=open]:ring-4 data-[state=open]:ring-emerald-500/10",
                  )}
                >
                  <DesignSelect.Value placeholder="Pick a role" />
                </DesignSelect.Trigger>
                <DesignSelect.Content>
                  {roleOptions.map((r) => (
                    <DesignSelect.Item key={r} value={r}>
                      {r}
                    </DesignSelect.Item>
                  ))}
                </DesignSelect.Content>
              </DesignSelect.Root>
            </Field>
          </DesignDialog.Body>
          <DesignDialog.Footer>
            <DesignDialog.Close asChild>
              <button
                type="button"
                className="h-12 px-5 rounded-xl text-[14px] font-semibold text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-all duration-150"
              >
                Cancel
              </button>
            </DesignDialog.Close>
            <button
              type="submit"
              disabled={loading || unchanged}
              className="h-12 px-7 inline-flex items-center gap-2 rounded-xl bg-zinc-900 text-white text-[15px] font-semibold shadow-lg shadow-zinc-900/20 hover:bg-black hover:shadow-zinc-900/30 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
            >
              <UserPlus01 className="size-4" aria-hidden />
              {loading ? "Saving…" : "Save"}
            </button>
          </DesignDialog.Footer>
        </form>
      </DesignDialog.Content>
    </DesignDialog.Root>
  )
}
